import { Controller } from '@hotwired/stimulus'

interface DirectUploadProgressEvent extends Event {
  detail: {
    progress: number
  }
}

export default class ProgressBarController extends Controller<HTMLLabelElement> {
  declare labelsValue: Record<'done' | 'indeterminate' | 'loading', string>
  declare prefixValue: string
  declare progressValue: number
  declare suffixValue: string
  declare readonly prefixTarget: HTMLSpanElement
  declare readonly progressTarget: HTMLProgressElement
  declare readonly suffixTarget: HTMLSpanElement

  static targets = ['prefix', 'progress', 'suffix']
  static values = {
    labels: {
      type: Object,
      default: {
        done: 'Done',
        indeterminate: 'Processing…',
        loading: '${progressValue}%',
      },
    },
    prefix: String,
    progress: { type: Number, default: -1 },
    suffix: String,
  }

  // Lifecycle methods

  connect() {
    this.#updatePrefixTarget()
    this.#updateProgressTarget()
    this.#updateSuffixTarget()
  }

  // Value change methods

  prefixValueChanged = this.#updatePrefixTarget.bind(this)
  progressValueChanged = this.#updateProgressTarget.bind(this)
  suffixValueChanged = this.#updateSuffixTarget.bind(this)

  // Public methods

  update(event: DirectUploadProgressEvent) {
    this.progressValue = event.detail.progress
  }

  // Private methods

  #getTooltipText() {
    switch (this.progressValue) {
      case -1:
        return this.labelsValue.indeterminate
      case 100:
        return this.labelsValue.done
      default:
        return this.labelsValue.loading.replace('${progressValue}', this.progressValue.toString())
    }
  }

  #updatePrefixTarget() {
    this.prefixTarget.innerText = this.prefixValue
    this.prefixTarget.hidden = !this.prefixValue
  }

  #updateProgressTarget() {
    const done = this.progressValue === 100
    const indeterminate = this.progressValue === -1

    if (indeterminate) {
      this.progressTarget.removeAttribute('value')
      this.dispatch('indeterminate')
    } else {
      this.progressTarget.value = this.progressValue
      if (done) this.dispatch('done')
    }

    this.element.classList.toggle('done', done)
    this.progressTarget.dataset.tooltipText = this.#getTooltipText()
    this.dispatch('progress', { detail: { progress: this.progressValue } })
  }

  #updateSuffixTarget() {
    this.suffixTarget.innerText = this.suffixValue
    this.suffixTarget.hidden = !this.suffixValue
  }
}
